import { useEffect } from 'react';
import {
  isLiveComposition,
  numberInputBackstop,
  numberKeypressFilter,
} from 'helpers/numberKeypressFilter';
import {
  RATING_DIALOG_SCOPE,
  RATING_FIELD_SELECTOR,
} from 'pages/ahu-board/forms/selector/useRatingNumberKeypress';

/**
 * The AHU board's number-field character guard — a legacy binding the React
 * port dropped, restored.
 *
 * ★ THIS IS PARITY WORK, NOT A NEW BEHAVIOUR. Legacy's AHU-board MainView is
 * `el: '#ahu-board-content'` (`ahu/mvc/view/main.js:70`, pre-React) and its
 * events hash binds, at :92:
 *
 *   'keypress input[data-type="number"], input[data-formatter_type="decimal"],
 *             input[data-formatter_type="integer"]': 'numberKeypress'
 *
 * — the selector below, verbatim. `PHASE6_AHUBOARD_INVENTORY.md` §2.4 records
 * it. Legacy has FIVE such hosts (AHU board, project board, CMS,
 * component_selector, kinetix_bot); the port carried four and lost this one,
 * so every number box in the Data Bar, the component and accessory forms and
 * the board's popups took letters. Typed `nnnn` sat in X-Offset exactly as
 * typed; the blur handlers then read it back as `NaN`, declined to save, and
 * because the inputs are uncontrolled and keyed on the STORED value the junk
 * stayed on screen looking committed until the board was reloaded.
 *
 * ★ HOW IT IS SCOPED, precisely: this is PAGE-scoped with exclusions, not
 * root-scoped. The listeners sit on `document` and live as long as the board
 * route, and `guarded()` is a deny-list — anything matching the selector is
 * filtered unless it sits in one of the rating scopes below. That is a weaker
 * guarantee than `useGlobalInputBehaviors`'s positive `closest('#ksdm-content')`
 * gate, and it is the shape that produced the header-popup bug described on
 * `HEADER_RATING_POPUP` below, so
 * do not read the paragraph below as saying the hook cannot see non-board DOM.
 * It can: `GlobalHeader` and the board's several `createPortal` popups are all
 * siblings of `#ahu-board-content`, so a positive gate would have to enumerate
 * `#ksdm-content`, `#wrapper-div`, `#new_ahu_popup`, `#popup-only` AND every
 * body-portalled popup, and a root missed from that list loses its guard
 * silently. The deny-list fails the other way — loudly, as a popup that
 * filters when it should not — which is the failure you find. If a header or
 * global popup ever gains a `data-type="number"` box, exclude it here.
 *
 * ★ WHY IT IS STILL SCOPED TO THE BOARD AND NOT INSTALLED APP-WIDE.
 * Legacy's own answer is the shape to keep: five containers, five rules. One
 * document-level rule for every `input[data-type="number"]` in the SPA is not
 * safe, for two reasons found by reading the other hosts:
 *
 *  · CMS Admin's filter deliberately differs. `cms.main.js:331-368` allows `.`
 *    on EVERY numeric box, integer or not, and a leading `-` whatever
 *    `data-min` says; the shared filter blocks both. A single rule has to pick
 *    one, and either choice rewrites one host's behaviour.
 *  · `data-step` / `data-formatter_type` are not applied consistently enough to
 *    carry a global rule. Across the SPA ~20 `data-type="number"` boxes declare
 *    neither, and they are a genuine mix: `qty_stacked` and `mounting_angle`
 *    are integer-only, the CMS clearances are 2-dp. Whichever default a global
 *    rule picked would be wrong for half of them — blocking `.` in a clearance
 *    box, or allowing `1.5` sets of filters.
 *
 * Inside the board the markup IS consistent (every box declares a step or a
 * formatter, with `AmsForm`'s Position X/Y the one gap, fixed in the same
 * change), so the shared filter resolves each field correctly here.
 */
const NUMBER_FIELDS =
  'input[data-type="number"], input[data-formatter_type="decimal"], input[data-formatter_type="integer"]';

/**
 * The two rating-dialog roots are treated DIFFERENTLY, because legacy treated
 * them differently — this hook reaches one of them and not the other.
 *
 * `#component_rating_mode` is the dialog embedded in the board, and
 * `main.tmpl:322` renders it INSIDE `#ahu-board-content`, so legacy's board
 * binding caught its inputs as well as the component_selector's own. The two
 * selectors are not the same: `component_selector.js:447` (ported as
 * `useRatingNumberKeypress`) omits `data-formatter_type="integer"`, the board
 * binding includes it. So the exclusion here is the INTERSECTION — skip only
 * what the other hook actually matches — which leaves the dialog's
 * integer-only boxes (`PlateHeatExchangerSelector:3185`,
 * `EnergyWheelSelector:3303/3310`, `FilterSelector:3284/3432`,
 * `EnergyWheelRatedOutputs:610/620/739/752`) guarded exactly as legacy
 * guarded them, and nothing guarded twice.
 *
 * `.component-selector-popup` is the HEADER's Independent Rating popup, and
 * it is skipped WHOLESALE. Legacy appends it to `body`
 * (`header.js:522-531 openComponentSelectorPopup`) and `ahuBase.html` puts
 * the header outside `#ahu-board-content` (:13 vs :37), so the board binding
 * never reached it — only the component_selector's own did. Applying the
 * intersection rule here too (as the first cut of this hook did) filtered its
 * integer-only boxes whenever the AHU board happened to be the page
 * underneath, and left them unfiltered when the same popup was opened from
 * the dashboard. Same popup, two behaviours, neither of them legacy's.
 */
/** Header Independent Rating popup — outside legacy's board root entirely. */
const HEADER_RATING_POPUP = '.component-selector-popup';

export function useBoardNumberInputs(): void {
  useEffect(() => {
    const guarded = (e: Event): HTMLInputElement | null => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return null;
      if (t.disabled || t.readOnly) return null;
      if (!t.matches(NUMBER_FIELDS)) return null;
      if (t.closest(HEADER_RATING_POPUP)) return null;
      if (t.closest(RATING_DIALOG_SCOPE) && t.matches(RATING_FIELD_SELECTOR)) return null;
      return t;
    };

    const onKeyPress = (e: KeyboardEvent) => {
      const input = guarded(e);
      if (input) numberKeypressFilter(e, input);
    };
    // Everything `keypress` cannot see — paste, drop, autofill, IME. Bound to
    // `compositionend` as well as `input`, and skipped mid-composition: see
    // `isLiveComposition`.
    const onRepair = (e: Event) => {
      if (isLiveComposition(e)) return;
      const input = guarded(e);
      if (input) numberInputBackstop(input);
    };

    document.addEventListener('keypress', onKeyPress);
    document.addEventListener('input', onRepair);
    document.addEventListener('compositionend', onRepair);
    return () => {
      document.removeEventListener('keypress', onKeyPress);
      document.removeEventListener('input', onRepair);
      document.removeEventListener('compositionend', onRepair);
    };
  }, []);
}
